// ============================================================
// เนื้อหาหน้าเว็บขาย (/sale-page-contents)
// ------------------------------------------------------------
// แก้เนื้อหาทุกส่วนของหน้าแรกเว็บไซต์ประกาศขาย/ให้เช่า (CDS-Sale-Landing-Page /overview)
// ที่ไม่ได้มาจากข้อมูลห้องจริง — ภาพพื้นหลัง พาดหัว ปุ่มทางลัด แบนเนอร์ รีวิว จุดเด่น
// ตัวเลข FAQ แถบชวนติดต่อ โซเชียล ฟุตเตอร์ (ตั้งค่าแต่ละส่วนดู components/sale-content/sections.js)
//
// ★ ขอบเขตข้อมูล (ตรงกับ GLOBAL_SECTIONS / scopeOf ฝั่ง backend)
//   ทุก section เป็น "ตั้งค่ากลาง" ชุดเดียวทั้งเว็บ (project_id = null) แสดงเหมือนกันทุกโครงการและหน้ารวม
//   หน้านี้อยู่ในเมนูผู้ดูแลระบบ (global.menu type admin) ไม่อยู่ในเมนูฝั่งโครงการ
//   เดิม section อื่นนอกจากภาพพื้นหลังผูกรายโครงการ — แถวรายโครงการเดิมไม่ถูกดึงมาแสดง/แก้แล้ว
//
// ★ 1 แถว = 1 ชิ้นเนื้อหา เรียงด้วย sequence · ลบ = soft delete ตาม API
// ★ เลื่อนลำดับยิง PUT /reorder ครั้งเดียว (backend อัปเดตทั้งชุดใน transaction เดียว ไม่ค้างครึ่งทาง)
//   เรียงเลขใหม่ 1..n ทั้งชุด กันกรณีแถวเก่ามี sequence ซ้ำกันแล้วสลับไม่ขยับ
//
// ★ "ข้อมูลบริษัท" และ "ช่องทางติดต่อ" อยู่ในหน้า "การตั้งค่าการขาย (โครงการ)"
//   (src/page/admin/sale-project-settings.js) ไม่ได้แก้ที่นี่
// ============================================================

import React, { Component } from "react";
import Resizer from "react-image-file-resizer";

import Navbar from "../../components/Navbar.js";
import { loading, GET, POST, PUT, DELETE, Modal, alert } from "../../components/CustomComponent.js";

import BackgroundSection from "../../components/sale-content/BackgroundSection.js";
import ContentItemList from "../../components/sale-content/ContentItemList.js";
import ContentFormModal from "../../components/sale-content/ContentFormModal.js";
import { MENU_GROUPS, sectionOf, isGlobalSection } from "../../components/sale-content/sections.js";

import "../../assets/css/bootstrap.css";
import "../../assets/css/style.css";
import "../../assets/css/property.css";
import "../../assets/css/property_form.css";
import "../../assets/css/property-theme-cds.css";
import "../../assets/css/sale-content.css";

const API = "api/admin/sale-page-contents";
const SALE_SITE_URL = (process.env.REACT_APP_SALE_SITE_URL || "").replace(/\/+$/, "");

// ย่อภาพพื้นหลังก่อนส่ง แล้วแปลงเป็น base64 — backend อัปขึ้น S3 ให้เอง (DB เก็บแค่ URL)
// ★ ภาพพื้นหลังกินเต็มจอ จึงย่อที่ 1920 ไม่ใช่ 1600 เหมือนรูปในเนื้อหา
const resizeBackground = (file) =>
  new Promise((resolve) => {
    Resizer.imageFileResizer(file, 1920, 1920, "JPEG", 88, 0, (uri) => resolve(uri), "base64");
  });

// เรียงตาม sequence — backend เรียง sequence, id มาแล้ว sort แบบ stable จึงคงลำดับ id ไว้
const bySequence = (rows) => [...rows].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));

const nextSequence = (rows) => rows.reduce((max, i) => Math.max(max, i.sequence || 0), 0) + 1;

export default class SalePageContents extends Component {
  state = {
    active: "background",
    rows: {},              // { [section]: [...] }
    loaded: false,
    errorMessage: "",

    working: "",           // uid ที่กำลังบันทึก / "reorder" (กันกดรัว)
    uploading: false,

    form_show: false,
    form_item: null,       // null = เพิ่มใหม่
    saving: false,

    delete_target: null,
  };

  componentDidMount() {
    if (!localStorage.getItem("cds-acc-tk")) {
      localStorage.clear();
      window.location.href = "/";
      return;
    }
    loading(false);
    this.load();
  }

  // ---------- ข้อมูล ----------

  // โหลดทุก section ครั้งเดียว (ไม่ส่ง section = ทุก section ของกลาง)
  load = async () => {
    loading(true);
    const result = await GET(API);
    loading(false);

    if (!result?.status) {
      this.setState({ loaded: true, errorMessage: result?.message || "โหลดข้อมูลไม่สำเร็จ (อาจไม่มีสิทธิ์ใช้งานเมนูนี้)" });
      return;
    }

    const rows = {};
    (result.data?.data || []).forEach((row) => {
      // ★ ข้ามแถวรายโครงการเดิม (project_id ไม่ว่าง) และ section ที่หน้านี้ไม่รู้จัก (เช่น gallery ที่ตัดทิ้งแล้ว)
      if (row.is_global === false || !sectionOf(row.section)) return;
      (rows[row.section] = rows[row.section] || []).push(row);
    });
    Object.keys(rows).forEach((key) => {
      rows[key] = bySequence(rows[key]);
    });

    this.setState({ rows, loaded: true, errorMessage: "" });
  };

  reloadSection = async (key) => {
    const result = await GET(API, { section: key });
    if (!result?.status) return;
    this.setState((prev) => ({ rows: { ...prev.rows, [key]: bySequence(result.data?.data || []) } }));
  };

  itemsOf = (key) => this.state.rows[key] || [];

  // ทุก section เป็นของกลาง แก้ได้เสมอ (สิทธิ์ตรวจที่ backend)
  canEdit = (key) => isGlobalSection(key);

  // ---------- เพิ่ม / แก้ ----------

  openCreate = () => this.setState({ form_show: true, form_item: null });

  openEdit = (item) => this.setState({ form_show: true, form_item: item });

  closeForm = () => {
    if (this.state.saving) return;
    this.setState({ form_show: false, form_item: null });
  };

  saveForm = async (payload) => {
    const { active, form_item } = this.state;
    this.setState({ saving: true });
    loading(true);

    // ★ แก้ไขไม่ส่ง section ไป — ไม่ย้าย section ข้ามกัน (เคยทำให้ project_id ค้างผิดขอบเขต)
    const result = form_item
      ? await PUT(`${API}/${form_item.uid}`, payload)
      : await POST(API, { ...payload, section: active, sequence: nextSequence(this.itemsOf(active)), is_active: true });

    await this.reloadSection(active);
    loading(false);
    this.setState({ saving: false });

    if (result?.status) {
      this.setState({ form_show: false, form_item: null });
      alert("", "success", "สำเร็จ", "บันทึกเรียบร้อย หน้าเว็บจะเห็นเนื้อหาใหม่เมื่อโหลดหน้าใหม่");
    }
  };

  // ---------- ภาพพื้นหลัง (อัปหลายไฟล์) ----------

  pickBackgrounds = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;

    this.setState({ uploading: true });
    loading(true);

    // ต่อท้ายจากลำดับสุดท้ายที่มีอยู่ ภาพใหม่จะได้ไม่แทรกกลางสไลด์
    let sequence = nextSequence(this.itemsOf("background")) - 1;
    let failed = 0;

    for (const file of files) {
      const base64 = await resizeBackground(file);
      sequence += 1;
      const result = await POST(API, {
        section: "background",
        sequence,
        title: file.name ? file.name.replace(/\.[^.]+$/, "") : "ภาพพื้นหลัง",
        image_url: base64,
        is_active: true,
      });
      if (!result?.status) failed += 1;
    }

    await this.reloadSection("background");
    loading(false);
    this.setState({ uploading: false });

    // อัปหลายไฟล์พร้อมกัน ถ้าพลาดบางไฟล์ต้องบอกจำนวน ไม่ใช่เงียบ ๆ
    if (failed) alert("", "warning", "อัปโหลดไม่ครบ", `มี ${failed} จาก ${files.length} ภาพที่อัปโหลดไม่สำเร็จ`);
  };

  // ---------- ทีละแถว ----------

  toggleActive = async (key, item) => {
    this.setState({ working: item.uid });
    loading(true);
    await PUT(`${API}/${item.uid}`, { is_active: !item.is_active });
    await this.reloadSection(key);
    loading(false);
    this.setState({ working: "" });
  };

  // เลื่อนลำดับ = สลับตำแหน่งกับชิ้นข้างเคียง แล้วเรียงเลข 1..n ใหม่ทั้งชุด
  // ส่งเฉพาะแถวที่เลขเปลี่ยนไป PUT /reorder ครั้งเดียว (สำเร็จหรือไม่สำเร็จทั้งชุด)
  move = async (key, index, direction) => {
    const list = [...this.itemsOf(key)];
    const target = index + direction;
    if (target < 0 || target >= list.length) return;
    [list[index], list[target]] = [list[target], list[index]];

    const items = list
      .map((row, i) => ({ uid: row.uid, sequence: i + 1, before: row.sequence || 0 }))
      .filter((row) => row.before !== row.sequence)
      .map(({ uid, sequence }) => ({ uid, sequence }));
    if (!items.length) return;

    this.setState({ working: "reorder" });
    loading(true);
    const result = await PUT(`${API}/reorder`, { items });
    await this.reloadSection(key);
    loading(false);
    this.setState({ working: "" });
    if (!result?.status) alert("", "warning", "จัดลำดับไม่สำเร็จ", "ลำดับยังเป็นแบบเดิม กรุณาลองใหม่อีกครั้ง");
  };

  remove = async () => {
    const { delete_target: target, active } = this.state;
    if (!target) return;
    loading(true);
    const result = await DELETE(`${API}/${target.uid}`);
    if (result?.status) await this.reloadSection(active);
    loading(false);
    if (result?.status) this.setState({ delete_target: null });
  };

  // เปิดหน้าแรกของเว็บขายในแท็บใหม่ (เฉพาะที่เปิดแสดงและบันทึกแล้วเท่านั้นที่เห็น)
  openPreview = () => {
    if (!SALE_SITE_URL) {
      alert("", "warning", "ยังไม่ได้ตั้งค่าหน้าตัวอย่างเว็บ", "ระบบยังไม่ได้ตั้งค่า URL ของหน้า Sale Page กรุณาติดต่อผู้ดูแลระบบ");
      return;
    }
    window.open(`${SALE_SITE_URL}/overview`, "_blank");
  };

  // ---------- ชิ้นส่วน ----------

  renderMenu = () => {
    const { active } = this.state;
    return (
      <div className="prop-card prop-in">
        <div className="p-2">
          {MENU_GROUPS.map((group) => (
            <div key={group.label} className="mb-2">
              <div className="text-12 text-secondary fw-bold px-2 pt-2 pb-1">{group.label}</div>
              {group.keys.map((key) => {
                const section = sectionOf(key);
                const items = this.itemsOf(key);
                const shown = items.filter((i) => i.is_active).length;
                const locked = !this.canEdit(key);
                const isActive = active === key;
                return (
                  <button
                    key={key}
                    type="button"
                    data-testid={`sc-menu-${key}`}
                    className={`btn w-100 text-start p-2 px-3 mb-1 rounded-3 ${isActive ? "btn-success" : "btn-outline-dark border-0"} ${locked ? "opacity-50" : ""}`}
                    onClick={() => this.setState({ active: key })}
                  >
                    <div className="d-flex justify-content-between align-items-center gap-2">
                      <span className="fw-bold">{section.label}</span>
                      {locked ? (
                        <span className="icon text-12">{""}</span>
                      ) : (
                        <span className={`sc-count ${isActive ? "is-active" : ""}`}>{shown}/{items.length}</span>
                      )}
                    </div>
                    <div className={`text-12 ${isActive ? "text-white-50" : "text-secondary"}`}>{section.desc}</div>
                  </button>
                );
              })}
            </div>
          ))}
          <div className="text-12 text-secondary px-2 pb-2">ตัวเลข = แสดงบนเว็บ / ทั้งหมด</div>
        </div>
      </div>
    );
  };

  renderSectionHead = (section) => {
    const global = isGlobalSection(section.key);
    const items = this.itemsOf(section.key);
    // section แบบชิ้นเดียว มีแล้วให้กดแก้ชิ้นเดิม ไม่ต้องเพิ่มซ้ำ
    const canAdd = !section.custom && this.canEdit(section.key) && !(section.single && items.length);

    return (
      <div className="prop-card prop-in mb-3">
        <div className="p-4">
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
            <div style={{ minWidth: 0, flex: "1 1 320px" }}>
              <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                <h5 className="fw-bold mb-0">{section.label}</h5>
                {global && <span className="sc-scope sc-scope--global">มีผลทุกโครงการ</span>}
              </div>
              <div className="text-14 text-secondary">
                <b>แสดงที่:</b> {section.where}
              </div>
              <div className="text-12 text-secondary mt-1">
                <b>ถ้าไม่มีรายการที่เปิดแสดง:</b> {section.webEmpty}
              </div>
              {section.single && (
                <div className="text-12 text-secondary mt-1">
                  <b>ใช้ชิ้นเดียว:</b> หน้าเว็บหยิบเฉพาะชิ้นแรกที่เปิดแสดงอยู่
                </div>
              )}
              {!!section.note && <div className="text-12 text-secondary mt-1">{section.note}</div>}
              {global && (
                <div className="text-12 text-warning mt-2">
                  แก้ส่วนนี้แล้วเปลี่ยนทั้งเว็บ ไม่ว่าลูกค้าจะดูโครงการไหน
                </div>
              )}
            </div>
            {canAdd && (
              <button type="button" className="prop-btn" data-testid="sc-add" onClick={this.openCreate}>
                <span className="icon text-14">{""}</span> เพิ่ม{section.label}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  renderSection = () => {
    const { active, working, uploading } = this.state;
    const section = sectionOf(active);
    if (!section) return null;
    const items = this.itemsOf(active);

    if (section.custom === "background") {
      return (
        <>
          {this.renderSectionHead(section)}
          <BackgroundSection
            items={items}
            uploading={uploading}
            working={working}
            onPick={this.pickBackgrounds}
            onMove={(index, dir) => this.move(active, index, dir)}
            onToggle={(item) => this.toggleActive(active, item)}
            onDelete={(item) => this.setState({ delete_target: item })}
          />
        </>
      );
    }

    return (
      <>
        {this.renderSectionHead(section)}
        {items.length ? (
          <ContentItemList
            section={section}
            items={items}
            working={working}
            onEdit={this.openEdit}
            onToggle={(item) => this.toggleActive(active, item)}
            onMove={(index, dir) => this.move(active, index, dir)}
            onDelete={(item) => this.setState({ delete_target: item })}
          />
        ) : (
          <div className="prop-card prop-in p-5 text-center text-secondary">
            <div className="fw-bold mb-1">ยังไม่มีรายการ</div>
            <div className="text-14">ตอนนี้หน้าเว็บ: {section.webEmpty}</div>
            <div className="mt-3">
              <button type="button" className="prop-btn" onClick={this.openCreate}>
                <span className="icon text-14">{""}</span> เพิ่ม{section.label}
              </button>
            </div>
          </div>
        )}
      </>
    );
  };

  render() {
    const s = this.state;
    const section = sectionOf(s.active);
    const projectLabel = "มีผลทุกโครงการ";

    return (
      <div className="w-100 min-vh-100 prop-page">
        {/* ★ ตั้งค่ากลาง — อยู่ในเมนูผู้ดูแลระบบเท่านั้น */}
        <Navbar page1="การตั้งค่า" page2="เนื้อหาหน้าเว็บขาย" />

        <div className="body p-4">
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3 prop-in">
            <div>
              <h3 className="fw-bold mb-1 text-dark">เนื้อหาหน้าเว็บขาย</h3>
              <div className="text-secondary text-14">
                ตั้งค่ากลาง · แก้ข้อความ รูป และลำดับของส่วนต่าง ๆ บนหน้าแรกเว็บไซต์ประกาศขาย/ให้เช่า (มีผลทุกโครงการ)
              </div>
            </div>
            <button type="button" className="prop-btn prop-btn--ghost" onClick={this.openPreview}>
              <span className="icon text-14">{""}</span> ดูหน้าเว็บจริง
            </button>
          </div>

          <div className="row g-3">
            <div className="col-12 col-lg-4 col-xl-3">{this.renderMenu()}</div>
            <div className="col-12 col-lg-8 col-xl-9">
              {!s.loaded ? (
                <div className="prop-card prop-in p-5 text-center text-secondary">กำลังโหลดข้อมูล...</div>
              ) : s.errorMessage ? (
                <div className="prop-card prop-in p-5 text-center text-secondary">{s.errorMessage}</div>
              ) : (
                this.renderSection()
              )}
            </div>
          </div>
        </div>

        <ContentFormModal
          show={s.form_show}
          section={section}
          item={s.form_item}
          saving={s.saving}
          projectLabel={projectLabel}
          onHide={this.closeForm}
          onSubmit={this.saveForm}
        />

        <Modal show={!!s.delete_target} centered onHide={() => this.setState({ delete_target: null })}>
          <Modal.Header>
            <Modal.Title><b>ยืนยันการลบ</b></Modal.Title>
          </Modal.Header>
          <Modal.Body>
            ลบ "{s.delete_target?.title || section?.label}" แล้วจะหายจากหน้าเว็บทันที
            {isGlobalSection(s.active) ? " (มีผลทุกโครงการ)" : ""}
            <div className="text-12 text-secondary mt-2">ถ้าแค่ต้องการพักไว้ก่อน ใช้ปุ่ม "ซ่อน" แทนได้</div>
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="prop-btn prop-btn--ghost" onClick={() => this.setState({ delete_target: null })}>
              ยกเลิก
            </button>
            <button type="button" className="prop-btn prop-btn--danger" onClick={this.remove}>ลบ</button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}
